import { get } from 'svelte/store';
import { home_data } from 'src/stores/account/user';
import { save_home } from 'src/helpers/home_helpers';
import { dashboards } from 'src/stores/features/dashboards';
import { postReq } from 'src/services/http';

/**
 * Camera Monitoring Dashboard Settings
 * Persisted via home_data (interface/home/save endpoint)
 */

/**
 * Grid layout for the camera grid.
 * - `1x1`–`4x4`: uniform grids with N columns.
 * - `focus`: spotlight layout — one large camera spanning a 2x2 area with up to
 *   5 smaller cameras filling the rest of a 3x3 grid.
 */
export type GridLayout = '1x1' | '2x2' | '3x3' | '4x4' | 'focus';

export interface CameraMonitoringSettings {
	/** Preferred grid layout (columns) */
	preferredLayout: GridLayout;
	/** Whether to hide offline cameras from the grid */
	hideOfflineCameras: boolean;
	/** Ordered array of selected camera device IDs (order = display order, presence = selected) */
	cameras: string[];
	/** Device id of the spotlighted camera in the `focus` layout (empty = first camera) */
	focusedCameraId: string;
}

/** Default settings for camera monitoring dashboard */
export const defaultCameraMonitoringSettings: CameraMonitoringSettings = {
	preferredLayout: '2x2',
	hideOfflineCameras: false,
	cameras: [], // Empty = show all cameras in default order
	focusedCameraId: '',
};

/** Key used in home_data store */
const CAMERA_MONITORING_KEY = 'cameraMonitoring';
export const CAMERA_DEFAULT_DASHBOARD_ID = -11;

/**
 * Get camera monitoring settings from home_data
 * Falls back to defaults if not set
 */
/**
 * Normalize cameras value to array
 * Backend may serialize arrays as objects with numeric keys: {"0": "id1", "1": "id2"}
 */
function normalizeCamerasArray(cameras: unknown): string[] {
	if (Array.isArray(cameras)) {
		return cameras;
	}
	if (cameras && typeof cameras === 'object') {
		// Convert object with numeric keys back to array
		return Object.values(cameras as Record<string, string>);
	}
	return [];
}

export function getCameraMonitoringSettings(
	homeData,
	dashboardId?: string | number,
): CameraMonitoringSettings {
	const dashboardSettings =
		dashboardId !== undefined ? get(dashboards)?.[dashboardId]?.cameraSettings : undefined;
	if (dashboardSettings) {
		return {
			...defaultCameraMonitoringSettings,
			...dashboardSettings,
			cameras: normalizeCamerasArray(dashboardSettings.cameras),
		};
	}

	const stored = homeData?.[CAMERA_MONITORING_KEY];

	if (!stored) {
		return { ...defaultCameraMonitoringSettings };
	}

	// Merge with defaults to ensure all fields exist (handles schema evolution)
	return {
		...defaultCameraMonitoringSettings,
		...stored,
		// Ensure cameras is always an array (handles backend object serialization)
		cameras: normalizeCamerasArray(stored.cameras),
	};
}

/**
 * Save camera monitoring settings to home_data
 * Persists to backend via interface/home/save
 */
export async function saveCameraMonitoringSettings(
	settings: Partial<CameraMonitoringSettings>,
	dashboardId?: string | number,
): Promise<boolean> {
	const current = getCameraMonitoringSettings(get(home_data), dashboardId);
	const updated: CameraMonitoringSettings = {
		...current,
		...settings,
	};

	if (dashboardId !== undefined) {
		const all = get(dashboards);
		const dashboard = all[dashboardId];
		if (!dashboard) return false;
		const nextDashboard = {
			...dashboard,
			kind: 'camera',
			cameraSettings: updated,
		};
		dashboards.update({ ...all, [dashboardId]: nextDashboard });
		const res = await postReq('/interface/dash/update', {
			id: dashboardId,
			data: JSON.stringify(nextDashboard),
		});
		if (!res?.isok) dashboards.update({ ...get(dashboards), [dashboardId]: dashboard });
		return Boolean(res?.isok);
	}

	const res = await save_home(CAMERA_MONITORING_KEY, updated);
	return res?.isok ?? false;
}

/**
 * Update a single setting and persist
 */
export async function updateCameraMonitoringSetting<K extends keyof CameraMonitoringSettings>(
	key: K,
	value: CameraMonitoringSettings[K],
): Promise<boolean> {
	return saveCameraMonitoringSettings({ [key]: value });
}

/**
 * Reset camera monitoring settings to defaults
 */
export async function resetCameraMonitoringSettings(): Promise<boolean> {
	return saveCameraMonitoringSettings(defaultCameraMonitoringSettings);
}

export async function initializeCustomCameraDashboard(dashboardId: string | number): Promise<boolean> {
	const all = get(dashboards);
	const dashboard = all[dashboardId];
	if (!dashboard) return false;
	const updated = {
		...dashboard,
		kind: 'camera',
		protected: false,
		cameraSettings: { ...defaultCameraMonitoringSettings },
	};
	dashboards.update({ ...all, [dashboardId]: updated });
	const res = await postReq('/interface/dash/update', {
		id: dashboardId,
		data: JSON.stringify(updated),
	});
	if (!res?.isok) dashboards.update({ ...get(dashboards), [dashboardId]: dashboard });
	return Boolean(res?.isok);
}

export async function ensureDefaultCameraDashboard(): Promise<boolean> {
	const all = get(dashboards);
	const existing = all[CAMERA_DEFAULT_DASHBOARD_ID];
	if (existing?.kind === 'camera' && existing?.cameraSettings) return true;

	const settings = getCameraMonitoringSettings(get(home_data));
	const updated = {
		id: CAMERA_DEFAULT_DASHBOARD_ID,
		name: 'Camera',
		text: 'Camera',
		position: 0,
		kind: 'camera',
		protected: true,
		schemaVersion: 1,
		icon: 'fas fa-video',
		backgroundColor: 'color-0',
		rooms: {},
		groups: {},
		devices: {},
		scenes: {},
		dashWidgets: [],
		cameraSettings: settings,
		...existing,
	};
	updated.kind = 'camera';
	updated.protected = true;
	updated.cameraSettings = existing?.cameraSettings ?? settings;

	const res = await postReq('/interface/dash/update', {
		id: CAMERA_DEFAULT_DASHBOARD_ID,
		data: JSON.stringify(updated),
	});
	if (res?.isok) dashboards.update({ ...all, [CAMERA_DEFAULT_DASHBOARD_ID]: updated });
	return Boolean(res?.isok);
}
